import debug from "debug";
import * as THREE from "three";
import { TitleBlockSizes } from "./TitleBlockSizes";

import { CSSRendering } from "./CSSRendering"; // Assuming this is the updated class

const log = debug("app:image");

/**
 * Handles WebGL rendering, CSS rendering, and image generation for the title block.
 * @category Components
 * @subcategory TitleBlock
 */
export class WebGLRendering {
  /**
   * Constructs a new instance of WebGLRendering.
   * @param {Object} experience - The shared application context containing the scene, camera, renderer, and other resources.
   */
  constructor(experience) {
    this.experience = experience;
    this.eventHub = this.experience.eventHub;
    this.sizes = this.experience.sizes;
    this.scene = this.experience.scene;
    this.camera = this.experience.camera.instance;
    this.renderer = this.experience.renderer.instance;
    this.cssRenderer = this.experience.renderer.cssRenderer;
    this.titleBlockSizes = new TitleBlockSizes();
    this.canvas = document.getElementById("modelCanvas");
    this.capturing = false;
    this.scaleFactor = 2;
  }

  cleanup() {
    console.log("✅ Cleaning up WebGLRendering...");

    // Dispose of the WebGL renderer instance
    if (this.renderer) {
      this.renderer.dispose();
      console.log("✅ WebGL renderer disposed.");
    }

    // Dispose of cloned camera if exists
    if (this.cloneCamera) {
      this.scene.remove(this.cloneCamera);
      this.cloneCamera = null;
      console.log("✅ Cloned camera removed.");
    }

    // Dispose of WebGL renderer created in _renderWebGL
    if (this.webGLRenderer) {
      this.webGLRenderer.dispose();
      this.webGLRenderer = null;
      console.log("✅ Secondary WebGL renderer disposed.");
    }

    // Nullify object references to allow garbage collection
    this.experience = null;
    this.sizes = null;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.cssRenderer = null;
    this.titleBlockSizes = null;
    this.canvas = null;

    console.log("✅ WebGLRendering cleanup complete.");
  }

  /**
   * Captures an image of the scene, including WebGL and CSS renderings, and returns it as a blob and URL.
   * @param {Object} sizes - The dimensions and positions for rendering the image.
   * @returns {Promise<Object>} A promise that resolves with an object containing:
   * - {Blob} blob - The captured image as a blob.
   * - {string} url - A URL for the captured image.
   */
  async getImage(sizes) {
    this.camera.updateProjectionMatrix();
    log("Capture Title Block Called sizes", sizes);

    if (this.capturing) {
      console.warn("Already capturing...");
      return;
    }
    this.capturing = true;

    this.eventHub.emit("needsRender", "true"); // ✅ Ensure rendering before capture

    this.camera.updateProjectionMatrix();
    const cloneCamera = this.camera.clone();
    cloneCamera.position.copy(this.camera.position);
    cloneCamera.rotation.copy(this.camera.rotation);
    cloneCamera.quaternion.copy(this.camera.quaternion);
    this.scene.add(cloneCamera);

    this._setCamera(cloneCamera, sizes);
    const webGLRenderer = this._renderWebGL(cloneCamera, sizes);
    const result = await this._generateImage(
      webGLRenderer,
      cloneCamera,
      sizes,
      this.scaleFactor
    );

    this.scene.remove(cloneCamera);
    webGLRenderer.dispose();
    this.capturing = false;

    console.log(result);
    return result;
  }

  // calculates current ratio and converts {key:valueInPixels} to {key:valueInWorldUnits}

  /**
   * Converts dimensions from pixels to world units based on the camera projection.
   * @param {Object} sizes - The sizes in pixels.
   * @returns {Object} An object with sizes converted to world units and the pixel-to-world ratio.
   */
  _convertToWorldUnits(sizes) {
    const pixelToWorldRatio =
      this.canvas.clientHeight / (this.camera.top - this.camera.bottom);

    const sizesWorldUnits = Object.keys(sizes).reduce((acc, key) => {
      if (typeof sizes[key] === "number") {
        acc[key] = sizes[key] / pixelToWorldRatio;
      } else {
        acc[key] = sizes[key];
      }
      return acc;
    }, {});
    sizesWorldUnits.pixelToWorldRatio = pixelToWorldRatio;

    return sizesWorldUnits;
  }

  /**
   * Sets up the cloned camera to focus on the title block area.
   * @param {THREE.Camera} cloneCamera - The cloned camera.
   * @param {Object} sizes - The sizes in pixels.
   */
  _setCamera(cloneCamera, sizes) {
    const sizesWorldUnits = this._convertToWorldUnits(sizes);
    this.camera.updateProjectionMatrix();

    const x = sizes.width / 2;
    const y = sizes.top + sizes.height / 2;
    const ndcX = (x / this.canvas.clientWidth) * 2 - 1;
    const ndcY = 1 - (y / this.canvas.clientHeight) * 2;
    const ndcPos = new THREE.Vector3(ndcX, 0, ndcY);
    ndcPos.unproject(cloneCamera);

    cloneCamera.left = -sizesWorldUnits.width / 2;
    cloneCamera.right = sizesWorldUnits.width / 2;
    cloneCamera.top = sizesWorldUnits.height / 2;
    cloneCamera.bottom = -sizesWorldUnits.height / 2;
    cloneCamera.updateProjectionMatrix();

    cloneCamera.position.set(ndcPos.x, this.camera.position.y, ndcPos.z);
  }

  /**
   * Renders the WebGL content with the cloned camera and provided sizes.
   * @param {THREE.Camera} cloneCamera - The cloned camera.
   * @param {Object} sizes - The sizes in pixels.
   * @returns {THREE.WebGLRenderer} The new WebGL renderer instance.
   */
  _renderWebGL(cloneCamera, sizes) {
    const newWebGLRenderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
    });
    newWebGLRenderer.setClearColor(0xffffff, 1);
    newWebGLRenderer.setSize(
      sizes.width * this.scaleFactor,
      sizes.height * this.scaleFactor
    );
    newWebGLRenderer.setViewport(
      0,
      0,
      sizes.width * this.scaleFactor,
      sizes.height * this.scaleFactor
    );

    this.eventHub.emit("needsRender", "true"); // ✅ Trigger re-render before WebGL rendering
    newWebGLRenderer.render(this.scene, cloneCamera);

    return newWebGLRenderer;
  }

  // Combine WebGL render with any other data

  /**
   * Generates the final image by combining WebGL and CSS renderings.
   * @param {THREE.WebGLRenderer} webGLRenderer - The WebGL renderer instance.
   * @param {THREE.Camera} cloneCamera - The cloned camera.
   * @param {Object} sizes - The sizes in pixels.
   * @param {number} scaleFactor - The scale factor for rendering.
   * @returns {Promise<Object>} A promise that resolves with an object containing:
   * - {Blob} blob - The captured image as a blob.
   * - {string} url - A URL for the captured image.
   */
  async _generateImage(webGLRenderer, cloneCamera, sizes, scaleFactor) {
    const canvas = document.createElement("canvas");
    canvas.width = sizes.width * scaleFactor;
    canvas.height = sizes.height * scaleFactor;

    if (canvas.width === 0 || canvas.height === 0) {
      throw new Error("Canvas width or height is zero");
    }

    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Failed to get 2D context for canvas");
    }

    this.eventHub.emit("needsRender", "true"); // ✅ Render before capturing CSS overlay

    context.drawImage(webGLRenderer.domElement, 0, 0);

    const cssRendering = new CSSRendering(this.experience);
    await cssRendering.renderCSS({
      cloneCamera,
      canvas,
      context,
      sizes: this._convertToWorldUnits(sizes),
      scaleFactor,
    });

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          resolve({ blob, url });
        } else {
          reject(new Error("Failed to capture combined image"));
        }
      }, "image/png");
    });
  }
}
